import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Task, TTaskStatus } from '@entities/task/model/types/tasks';
import { useToggleState } from '@shared/hooks';
import { Tooltip } from '@shared/ui';
import { toggleModalStatus } from '@widgets/Modals/model/slice/addModalSlice';
import { toggleEditModalStatus } from '@widgets/Modals/model/slice/editModalSlice';

import cls from './TaskStatusSwitcherBlock.module.css';
import { TaskStatusSwitcher } from './ui/TaskStatusSwitcher';
import { useAppDispatch, useAppSelector } from '../../store';

export const TaskStatusSwitcherBlock = ({
    taskId,
    isDisabled,
}: {
    taskId?: string;
    isDisabled: boolean;
}) => {
    const { t } = useTranslation();
    const [isHovered, toggleHover] = useToggleState();
    const dispatch = useAppDispatch();
    const taskData: Task = useAppSelector((state) => {
        if (taskId) {
            return state.tasksReducer.entities[taskId];
        }
        return {};
    });
    const [taskSwitcherStatus, setTaskSwitcherStatus] = useState<TTaskStatus>(
        taskData?.context?.status || 'is_waiting'
    );
    const tooltipTextContent =
        taskSwitcherStatus === 'is_waiting'
            ? t('Features.TaskStatusSwitcherBlock.is_waiting.tooltip')
            : t('Features.TaskStatusSwitcherBlock.in_progress.tooltip');
    const handleStatusSwitch = (e: React.SyntheticEvent) => {
        if (isDisabled) return;
        e.stopPropagation();
        setTaskSwitcherStatus((prev) => (prev === 'is_waiting' ? 'in_progress' : 'is_waiting'));
    };
    useEffect(() => {
        if (!taskId) {
            dispatch(toggleModalStatus(taskSwitcherStatus));
            return;
        }
        dispatch(toggleEditModalStatus(taskSwitcherStatus));
    }, [taskId, dispatch, taskSwitcherStatus]);

    return (
        <div
            className={classNames(
                cls.TaskStatusSwitcherWrapper,
                taskSwitcherStatus === 'is_waiting' ? cls.WaitStatusBg : cls.WorkingStatusBg,
                isDisabled && cls.disabled
            )}
            onClick={handleStatusSwitch}
            onMouseEnter={toggleHover}
            onMouseLeave={toggleHover}
            id="task-status-switcher-block"
        >
            <Tooltip
                textContent={tooltipTextContent}
                classes={isHovered ? cls.tooltipPosition : cls.hidden}
            />

            <TaskStatusSwitcher
                isLoading={false}
                status={taskSwitcherStatus}
                classes={
                    taskSwitcherStatus === 'is_waiting' ? cls.WaitIconStyle : cls.WorkingIconStyle
                }
            />
            <span
                className={
                    taskSwitcherStatus === 'in_progress' ? cls.WorkingStatus : cls.WaitStatus
                }
            >
                {taskSwitcherStatus === 'in_progress'
                    ? t('Features.TaskStatusSwitcherBlock.in_progress.text')
                    : t('Features.TaskStatusSwitcherBlock.is_waiting.text')}
            </span>
        </div>
    );
};
